import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import './AdminDashboard.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function DatabaseManagement({ user, onLogout }) {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const navigate = useNavigate();

  useEffect(() => {
    loadBackups();
  }, []);

  const loadBackups = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/database-backups.php`, {
        credentials: 'include'
      });

      const data = await response.json();

      if (data.success) {
        setBackups(data.backups || []);
      } else {
        setError(data.error || 'Failed to load backups');
      }
    } catch (error) {
      setError('Failed to connect to server');
      console.error('Load backups error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    if (!window.confirm('Create a new database backup? This may take a few moments.')) {
      return;
    }

    setCreating(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${API_BASE_URL}/database-backup.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ action: 'download' })
      });

      if (response.ok) {
        // Get filename from Content-Disposition header
        const disposition = response.headers.get('Content-Disposition');
        let filename = 'backup.sql';
        if (disposition) {
          const matches = /filename="(.+)"/.exec(disposition);
          if (matches && matches[1]) {
            filename = matches[1];
          }
        }

        // Download file
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setSuccess('Backup created and downloaded successfully!');
        loadBackups(); // Reload list
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to create backup');
      }
    } catch (error) {
      setError('Failed to create backup: ' + error.message);
      console.error('Create backup error:', error);
    } finally {
      setCreating(false);
    }
  };

  const handleDownloadBackup = async (filename) => {
    try {
      const response = await fetch(`${API_BASE_URL}/database-backups.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ filename })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setSuccess('Backup downloaded successfully!');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to download backup');
      }
    } catch (error) {
      setError('Failed to download backup: ' + error.message);
      console.error('Download backup error:', error);
    }
  };

  const handleDeleteBackup = async (filename) => {
    if (!window.confirm(`Are you sure you want to delete backup: ${filename}?\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/database-backups.php`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ filename })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('Backup deleted successfully!');
        loadBackups(); // Reload list
      } else {
        setError(data.error || 'Failed to delete backup');
      }
    } catch (error) {
      setError('Failed to delete backup: ' + error.message);
      console.error('Delete backup error:', error);
    }
  };

  if (!user) {
    navigate('/admin');
    return null;
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="admin-dashboard">
        <div className="page-header">
          <div>
            <h1 className="page-title">🗄️ Database Management</h1>
            <p className="page-subtitle">Backup and restore your database</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={handleCreateBackup}
              disabled={creating}
              className="modern-btn primary"
              style={{ height: 'fit-content' }}
            >
              {creating ? '⏳ Creating Backup...' : '💾 Create New Backup'}
            </button>
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="modern-btn secondary"
              style={{ height: 'fit-content' }}
            >
              ← Back to Dashboard
            </button>
          </div>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div style={{
            padding: '16px',
            marginBottom: '20px',
            backgroundColor: '#d4edda',
            color: '#155724',
            border: '1px solid #c3e6cb',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span>✅</span>
            <span>{success}</span>
            <button
              onClick={() => setSuccess('')}
              style={{
                marginLeft: 'auto',
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: '#155724'
              }}
            >
              ×
            </button>
          </div>
        )}

        {error && (
          <div style={{
            padding: '16px',
            marginBottom: '20px',
            backgroundColor: '#f8d7da',
            color: '#721c24',
            border: '1px solid #f5c6cb',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}>
            <span>⚠️</span>
            <span>{error}</span>
            <button
              onClick={() => setError('')}
              style={{
                marginLeft: 'auto',
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: '#721c24'
              }}
            >
              ×
            </button>
          </div>
        )}

        {/* Info Box */}
        <div style={{
          padding: '20px',
          marginBottom: '24px',
          backgroundColor: '#e7f3ff',
          border: '1px solid #b3d9ff',
          borderRadius: '8px'
        }}>
          <h3 style={{ margin: '0 0 10px 0', color: '#0066cc' }}>ℹ️ Important Information</h3>
          <ul style={{ margin: '0', paddingLeft: '20px', color: '#004080' }}>
            <li>Regular backups protect your data from loss</li>
            <li>Download backups to your local computer for safety</li>
            <li>Test backup restoration on local database before production</li>
            <li>Backups include all tables, data, and indexes</li>
            <li>Server backups are stored in <code>/backups/</code> folder</li>
          </ul>
        </div>

        {/* Backups Table */}
        <div className="modern-card" style={{ padding: '0' }}>
          <div style={{
            padding: '20px',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <h2 style={{ margin: '0 0 5px 0', fontSize: '18px', color: '#1f2937' }}>
                📦 Available Backups
              </h2>
              <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                {loading ? 'Loading...' : `${backups.length} backup(s) found`}
              </p>
            </div>
            <button
              onClick={loadBackups}
              className="modern-btn secondary"
              style={{ padding: '8px 16px', fontSize: '14px' }}
              disabled={loading}
            >
              🔄 Refresh
            </button>
          </div>

          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              <div className="loading-spinner" style={{
                border: '4px solid #f3f3f3',
                borderTop: '4px solid #6366f1',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }}></div>
              Loading backups...
            </div>
          ) : backups.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
              <p style={{ fontSize: '16px', margin: '0 0 12px 0' }}>📭 No backups found</p>
              <p style={{ fontSize: '14px', margin: 0 }}>
                Create your first backup using the button above
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse'
              }}>
                <thead>
                  <tr style={{ backgroundColor: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '13px' }}>
                      #
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '13px' }}>
                      Filename
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '13px' }}>
                      Created Date
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: '13px' }}>
                      Size
                    </th>
                    <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#374151', fontSize: '13px' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {backups.map((backup, index) => (
                    <tr key={backup.filename} style={{
                      borderBottom: '1px solid #e5e7eb',
                      transition: 'background-color 0.2s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                    >
                      <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: '14px' }}>
                        {index + 1}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: '14px' }}>
                        <code style={{
                          backgroundColor: '#f3f4f6',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '13px',
                          color: '#1f2937'
                        }}>
                          {backup.filename}
                        </code>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: '14px' }}>
                        {backup.created_at}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#6b7280', fontSize: '14px' }}>
                        {backup.size_formatted}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <button
                            onClick={() => handleDownloadBackup(backup.filename)}
                            className="table-action-btn primary"
                            title="Download Backup"
                          >
                            ⬇️ Download
                          </button>
                          <button
                            onClick={() => handleDeleteBackup(backup.filename)}
                            className="table-action-btn danger"
                            title="Delete Backup"
                          >
                            🗑️ Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* How to Restore Section */}
        <div style={{
          marginTop: '24px',
          padding: '20px',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '8px'
        }}>
          <h3 style={{ margin: '0 0 12px 0', color: '#856404' }}>📖 How to Restore from Backup</h3>
          <ol style={{ margin: '0', paddingLeft: '20px', color: '#856404' }}>
            <li>Download the backup file you want to restore</li>
            <li>Go to phpMyAdmin on your hosting panel</li>
            <li>Select your database</li>
            <li>Click on <strong>Import</strong> tab</li>
            <li>Choose the downloaded .sql file</li>
            <li>Click <strong>Go</strong> to restore</li>
            <li>Verify all data has been restored correctly</li>
          </ol>
          <p style={{ margin: '12px 0 0 0', fontSize: '14px', fontWeight: 600 }}>
            ⚠️ Warning: Restoring will replace all current data with backup data
          </p>
        </div>
      </div>
    </Layout>
  );
}

export default DatabaseManagement;
